import BigNumber from 'bignumber.js'
import { kebabCase } from 'lodash'
import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from 'state'
import { Toast, toastTypes } from 'uikit'

import { getAddress } from 'utils/addressHelpers'
import { updatePriceList } from 'state/prices'

import { clear as clearToast, push as pushToast, remove as removeToast } from './actions'
import { PriceState, ProfileState, State } from './types'

import { useTokenUsdtPrice } from '../utils/useGetMJTUsdtLpPrice'


import tokens from 'constants/tokens'


export const useGetApiPrices = () => {
  const prices: PriceState['data'] = useSelector((state: State) => state.prices?.data)
  return prices
}

export const useGetApiPrice = (address: any) => {
  const prices = useGetApiPrices()
  if (!prices) {
    return '0'
  }

  return prices[address.toLowerCase()]
}

// Toasts
export const useToast = () => {
  const dispatch = useAppDispatch()
  const helpers = useMemo(() => {
    const push = (toast: Toast) => dispatch(pushToast(toast))

    return {
      toastError: (title: string, description?: string) => {
        return push({ id: kebabCase(title), type: toastTypes.DANGER, title, description })
      },
      toastInfo: (title: string, description?: string) => {
        return push({ id: kebabCase(title), type: toastTypes.INFO, title, description })
      },
      toastSuccess: (title: string, description?: string) => {
        return push({ id: kebabCase(title), type: toastTypes.SUCCESS, title, description })
      },
      toastWarning: (title: string, description?: string) => {
        return push({ id: kebabCase(title), type: toastTypes.WARNING, title, description })
      },
      push,
      remove: (id: string) => dispatch(removeToast(id)),
      clear: () => dispatch(clearToast()),
    }
  }, [dispatch])

  return helpers
}

export const useProfile = () => {
  const { isInitialized, isLoading, data, hasRegistered }: ProfileState = useSelector((state: State) => state.profile)
  return { profile: data, hasProfile: isInitialized && hasRegistered, isInitialized, isLoading }
}

// Prices
export const useFetchPriceList = () => {
  // const { slowRefresh } = useRefresh()
  const dispatch = useAppDispatch()

  // todo: add ifo coin price here
  const mjtPrice = useTokenUsdtPrice(getAddress(tokens.mjt.address))
  const wkcsPrice = useTokenUsdtPrice(getAddress(tokens.wkcs.address))
  const ethPrice = useTokenUsdtPrice(getAddress(tokens.eth.address))
  const btckPrice = useTokenUsdtPrice(getAddress(tokens.btck.address))

  const priceData = useMemo(() => {
    const data = {
      updated_at: new Date().getTime().toString(),
      data: {
        [getAddress(tokens.mjt.address)]: {
          symbol: 'MJT',
          price: mjtPrice,
        },
        [getAddress(tokens.wkcs.address)]: {
          symbol: 'WKCS',
          price: wkcsPrice,
        },
        [getAddress(tokens.btck.address)]: {
          symbol: 'BTCK',
          price: btckPrice,
        },
        [getAddress(tokens.usdt.address)]: {
          symbol: 'USDT',
          price: '1',
        },

        [getAddress(tokens.usdc.address)]: {
          symbol: 'USDC',
          price: '1',
        },
        [getAddress(tokens.eth.address)]: {
          symbol: 'ETH',
          price: ethPrice,
        },
      },
    }

    return {
      updated_at: data.updated_at,
      data: Object.keys(data.data).reduce((accum, token) => {
        return {
          ...accum,
          [token.toLowerCase()]: parseFloat(String(data.data[token].price) ?? '0'),
        }
      }, {}),
    }
  }, [mjtPrice, wkcsPrice, btckPrice, ethPrice])
  useEffect(() => {
    dispatch(updatePriceList(priceData))
  }, [dispatch, priceData])
}

export const usePriceMjtUSDT = (): BigNumber => {
  const price = useTokenUsdtPrice(getAddress(tokens.mjt.address))
  return price ? new BigNumber(price) : new BigNumber(0)
}

export const usePriceKcsUSDT = (): BigNumber => {
  const price = useTokenUsdtPrice(getAddress(tokens.wkcs.address))
  return price ? new BigNumber(price) : new BigNumber(0)
}

// Block
export const useBlock = () => {
  return useSelector((state: State) => state.block)
}

export const useInitialBlock = () => {
  return useSelector((state: State) => state.block.initialBlock)
}

export const usePriceListUpdateTime = (): string => {
  const updated_at = useSelector((state: State) => {
    return state.prices.lastUpdated
  })
  return updated_at
}
